import { useMemo, useState } from "react";
import {
  FaFileAlt,
  FaFilePdf,
  FaPlus,
  FaPen,
  FaTrash,
  FaIdCard,
} from "react-icons/fa";
import {
  PageShell,
  PageHeader,
  ListView,
  ListToolbar,
  ListSkeleton,
  EmptyState,
  Modal,
} from "../../../Components/shared";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import { useAlert } from "../../../context/AlertContext";
import { PERMISSION_LABELS } from "../../../utils/permissionCatalog";
import {
  useGetFactoryDocumentGroupsQuery,
  useGetFactoryDocumentsQuery,
  useDeleteFactoryDocumentMutation,
} from "../../../store";
import FactoryDocumentDrawer from "../../../Components/factory/storageRoles/FactoryDocumentDrawer";
import RowActionButton from "../../../Components/factory/storageRoles/RowActionButton";

const TABLE_HEADERS = [
  { label: "BELGE" },
  { label: "GRUP" },
  { label: "SÜRÜM" },
  { label: "BOYUT" },
  { label: "DURUM" },
  { label: "GÜNCELLENME" },
  { label: "", className: "text-right" },
];

const formatSize = (bytes) => {
  if (!bytes) return "-";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
};

const formatDate = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StatusBadge = ({ active }) => (
  <span
    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${
      active
        ? "bg-emerald-50 text-emerald-700 border-emerald-100"
        : "bg-gray-50 text-gray-500 border-gray-100"
    }`}
  >
    {active ? "Aktif" : "Pasif"}
  </span>
);

/**
 * Fabrika › Belgeler (D-071).
 *
 * Tablette kart okutan çalışanın okuyacağı PDF'leri yönetir. Görünürlük
 * belgenin GRUBUYLA belirlenir — grup bir `factory.docs.<...>` iznidir,
 * o izne sahip olan tablette görür. Panelde ham izin anahtarı GÖSTERİLMEZ,
 * Türkçe etiket gösterilir.
 */
export default function FactoryDocumentsPage() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { hasPermission } = usePermission();
  const canManage = hasPermission("factory.documents.manage");
  const { showSuccess, showError } = useAlert();

  const [groupFilter, setGroupFilter] = useState("all");
  const [drawer, setDrawer] = useState(null); // null | {} | { document }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: groups = [] } = useGetFactoryDocumentGroupsQuery(
    { token },
    { skip: !token }
  );
  const { data: documents = [], isLoading } = useGetFactoryDocumentsQuery(
    { token, permission: groupFilter },
    { skip: !token }
  );
  const [deleteDocument, { isLoading: deleting }] =
    useDeleteFactoryDocumentMutation();

  const groupLabel = (key) => PERMISSION_LABELS[key] || key;

  // Grup filtresi — tek grup varken sekme göstermenin anlamı yok.
  const filterTabs = useMemo(() => {
    if (groups.length < 2) return null;
    return [
      { id: "all", label: "Tümü" },
      ...groups.map((g) => ({ id: g.key, label: groupLabel(g.key) })),
    ];
  }, [groups]);

  const handleDelete = async () => {
    try {
      await deleteDocument({ token, id: deleteTarget._id }).unwrap();
      showSuccess("Belge silindi");
      setDeleteTarget(null);
    } catch (err) {
      showError(err?.data?.message || "Belge silinemedi");
    }
  };

  return (
    <PageShell>
      <div className="px-3 py-2.5 sm:p-4 md:p-6 border-b border-gray-100 shrink-0">
        <PageHeader
          icon={FaFileAlt}
          iconColor="indigo"
          title="Fabrika Belgeleri"
          subtitle="Tablette kart okutan çalışanın göreceği PDF'ler"
          stats={[{ label: "Belge", value: documents.length }]}
          isLoading={isLoading}
          mobileSlotId="factory-documents-mobile-slot"
        />
      </div>

      <ListToolbar
        mobileFilterSlotId="factory-documents-mobile-slot"
        icon={FaFilePdf}
        count={documents.length}
        countLabel="belge"
        isLoading={isLoading}
        filterTabs={
          filterTabs
            ? {
                filters: filterTabs,
                activeFilter: groupFilter,
                onChange: setGroupFilter,
                totalCount: documents.length,
                isLoading,
                color: "indigo",
              }
            : undefined
        }
        actionItems={
          canManage
            ? [
                {
                  label: "Belge Yükle",
                  mobileLabel: "Yükle",
                  icon: FaPlus,
                  variant: "primary",
                  onClick: () => setDrawer({}),
                },
              ]
            : []
        }
      />

      <ListView
        headers={TABLE_HEADERS}
        data={documents}
        isLoading={isLoading}
        loadingNode={
          <ListSkeleton
            columns={TABLE_HEADERS.map((h) => ({ label: h.label }))}
            rowCount={10}
            message="Belgeler yükleniyor..."
          />
        }
        empty={
          <EmptyState
            message="Belge bulunamadı"
            suggestion={
              canManage
                ? "Sağ üstteki 'Belge Yükle' ile ilk belgeyi ekleyin"
                : "Bu grupta henüz belge yok"
            }
          />
        }
        renderRow={(doc) => (
          <tr
            key={doc._id}
            className="border-b border-gray-100 hover:bg-gray-50"
          >
            <td className="px-4 py-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 text-red-600 shrink-0">
                  <FaFilePdf className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {doc.title}
                  </p>
                  {doc.description && (
                    <p className="text-xs text-gray-500 truncate">
                      {doc.description}
                    </p>
                  )}
                </div>
              </div>
            </td>
            <td className="px-4 py-3">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                <FaIdCard className="w-3 h-3 shrink-0" />
                {groupLabel(doc.permission)}
              </span>
            </td>
            <td className="px-4 py-3 text-sm text-gray-600">
              {doc.version ? `v${doc.version}` : "-"}
            </td>
            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
              {formatSize(doc.fileSize)}
            </td>
            <td className="px-4 py-3">
              <StatusBadge active={doc.isActive !== false} />
            </td>
            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
              {formatDate(doc.updatedAt)}
            </td>
            <td className="px-4 py-3">
              <div className="flex justify-end gap-1.5">
                {canManage && (
                  <>
                    <RowActionButton onClick={() => setDrawer({ document: doc })}>
                      <FaPen className="w-3 h-3" />
                    </RowActionButton>
                    <RowActionButton
                      variant="danger"
                      onClick={() => setDeleteTarget(doc)}
                    >
                      <FaTrash className="w-3 h-3" />
                    </RowActionButton>
                  </>
                )}
              </div>
            </td>
          </tr>
        )}
        renderMobileCard={(doc) => (
          <div
            key={doc._id}
            className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col gap-2"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 text-red-600 shrink-0">
                  <FaFilePdf className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {doc.title}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {groupLabel(doc.permission)}
                  </p>
                </div>
              </div>
              <StatusBadge active={doc.isActive !== false} />
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                {doc.version ? `v${doc.version} · ` : ""}
                {formatSize(doc.fileSize)}
              </span>
              <span>{formatDate(doc.updatedAt)}</span>
            </div>
            {canManage && (
              <div className="flex gap-2 pt-1">
                <RowActionButton onClick={() => setDrawer({ document: doc })}>
                  Düzenle
                </RowActionButton>
                <RowActionButton
                  variant="danger"
                  onClick={() => setDeleteTarget(doc)}
                >
                  Sil
                </RowActionButton>
              </div>
            )}
          </div>
        )}
      />

      <FactoryDocumentDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        document={drawer?.document}
        groups={groups}
        groupLabels={PERMISSION_LABELS}
      />

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        maxWidth="md"
      >
        <Modal.Header
          title="Belgeyi Sil"
          subtitle={deleteTarget?.title || ""}
          onClose={() => setDeleteTarget(null)}
        />
        <Modal.Body>
          <p className="text-sm text-gray-600">
            Belge kalıcı olarak silinecek; dosya depolamadan da kaldırılır ve
            tablette artık görünmez. Belgeyi silmeden gizlemek isterseniz
            düzenleyip <span className="font-semibold">Pasif</span> yapın. Bu
            işlem geri alınamaz.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={() => setDeleteTarget(null)}
            className="px-3.5 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Vazgeç
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="px-3.5 py-2 text-sm font-semibold text-white bg-fifth rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {deleting ? "Siliniyor..." : "Kalıcı Olarak Sil"}
          </button>
        </Modal.Footer>
      </Modal>
    </PageShell>
  );
}
